import {
    IconButton,
    MenuItem,
    Stack,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import SalaryDataTable from './SalaryDataTable';
import SalaryFormSection from './SalaryFormSection';
import useRequest from '../../hooks/useRequest';
import type { SalaryEmployee, SalaryLeave } from '../../domain/salary/types';

interface Props {
    employees: SalaryEmployee[];
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
}

const LeavesTab = ({ employees }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [leaves, setLeaves] = useState<SalaryLeave[]>([]);
    const [form, setForm] = useState({
        employeeId: 0,
        startDate: '',
        endDate: '',
        type: 'paid' as 'paid' | 'unpaid',
    });

    const reload = useCallback(async () => {
        const response = await request<ApiResponse<SalaryLeave[]>>({ path: '/salary/leaves' });
        setLeaves(response.data ?? []);
    }, [request]);

    useEffect(() => {
        reload();
    }, [reload]);

    const handleAdd = async () => {
        if (!form.employeeId || !form.startDate || !form.endDate) return;
        await request({ path: '/salary/leaves', method: 'POST', params: form });
        setForm({ ...form, startDate: '', endDate: '' });
        await reload();
    };

    const handleDelete = async (id: number) => {
        await request({ path: `/salary/leaves/${id}`, method: 'DELETE' });
        await reload();
    };

    const employeeName = (id: number) => employees.find(e => e.id === id)?.name ?? '-';

    return (
        <Stack spacing={2}>
            <SalaryFormSection
                title={
                    <>
                        {t('salary.leaves.addTitle')}
                        <InfoHint title={t('salary.info.leaves.section')} />
                    </>
                }
                onAdd={handleAdd}
                addLabel={t('common.add')}
            >
                <TextField
                    size="small"
                    select
                    label={t('salary.schedule.employee')}
                    value={form.employeeId || ''}
                    onChange={e => setForm({ ...form, employeeId: Number(e.target.value) })}
                    sx={{ minWidth: 200 }}
                >
                    {employees.map(employee => (
                        <MenuItem key={employee.id} value={employee.id}>
                            {employee.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    type="date"
                    label={t('salary.leaves.startDate')}
                    value={form.startDate}
                    onChange={e => setForm({ ...form, startDate: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                    size="small"
                    type="date"
                    label={t('salary.leaves.endDate')}
                    value={form.endDate}
                    onChange={e => setForm({ ...form, endDate: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                    size="small"
                    select
                    label={t('salary.leaves.type')}
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value as 'paid' | 'unpaid' })}
                >
                    <MenuItem value="paid">{t('salary.leaves.paid')}</MenuItem>
                    <MenuItem value="unpaid">{t('salary.leaves.unpaid')}</MenuItem>
                </TextField>
                <InfoHint title={t('salary.info.leaves.type')} />
            </SalaryFormSection>

            <SalaryDataTable
                headers={
                    <>
                        <TableCell>{t('salary.schedule.employee')}</TableCell>
                        <TableCell>{t('salary.leaves.startDate')}</TableCell>
                        <TableCell>{t('salary.leaves.endDate')}</TableCell>
                        <TableCell>{t('salary.leaves.type')}</TableCell>
                    </>
                }
            >
                {leaves.map(leave => (
                    <TableRow key={leave.id}>
                        <TableCell>{employeeName(leave.employeeId)}</TableCell>
                        <TableCell>{leave.startDate}</TableCell>
                        <TableCell>{leave.endDate}</TableCell>
                        <TableCell>{t(`salary.leaves.${leave.type}`)}</TableCell>
                        <TableCell align="right">
                            <IconButton size="small" onClick={() => handleDelete(leave.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                ))}
                {leaves.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                                {t('salary.leaves.noData')}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </SalaryDataTable>
        </Stack>
    );
};

export default LeavesTab;
